import { createPortal } from "react-dom";
import PlaceDetailModule from "../PlaceDetailModule";
import type { PlaceObject } from "../types";
import type { Module } from "@/types/modules";
import { BREAKPOINTS } from "@/lib/breakpoints";

interface PlaceDetailOverlayProps {
  selectedPlace: PlaceObject;
  enrichedCard: any;
  isEnriching: boolean;
  isPublishing: boolean;
  publishedUrl: string | null;
  showOverlayGearMenu: boolean;
  module: Module;
  conversationId: string;
  placeDetailRef: React.RefObject<HTMLDivElement | null>;
  overlayGearRef: React.RefObject<HTMLDivElement | null>;
  onOverlayMouseDown: (e: React.MouseEvent) => void;
  onOverlayClick: (e: React.MouseEvent) => void;
  onClose: () => void;
  onToggleGearMenu: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onCopyPublishedLink: () => void;
  onDelete: () => void;
  onNoteSaved: (noteText: string) => void;
}

/**
 * Full-screen detail overlay for a selected place, portaled to
 * `#modal-portal-root`.
 *
 * Lifted verbatim from PlaceListModule's `selectedPlace && createPortal(...)`
 * block. All state (selectedPlace / enrichedCard / isEnriching / publishedUrl /
 * showOverlayGearMenu / isPublishing) and the overlay-click + enrichment hooks
 * stay in the orchestrator; this leaf renders the overlay chrome (gear menu,
 * publish/unpublish/copy/delete, close, loading spinner, the embedded
 * PlaceDetailModule, and the published-url banner) and fires callbacks.
 *
 * The orchestrator keeps the `selectedPlace && (...)` render guard, so this
 * component is only mounted when there is a selected place.
 */
export function PlaceDetailOverlay({
  selectedPlace,
  enrichedCard,
  isEnriching,
  isPublishing,
  publishedUrl,
  showOverlayGearMenu,
  module,
  conversationId,
  placeDetailRef,
  overlayGearRef,
  onOverlayMouseDown,
  onOverlayClick,
  onClose,
  onToggleGearMenu,
  onPublish,
  onUnpublish,
  onCopyPublishedLink,
  onDelete,
  onNoteSaved,
}: PlaceDetailOverlayProps) {
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: 9999,
        display: "flex",
        alignItems:
          window.innerWidth <= BREAKPOINTS.compactMax ? "stretch" : "center",
        justifyContent:
          window.innerWidth <= BREAKPOINTS.compactMax ? "stretch" : "center",
        padding: window.innerWidth <= BREAKPOINTS.compactMax ? "0" : "20px",
      }}
      onMouseDown={onOverlayMouseDown}
      onClick={onOverlayClick}
    >
      <div
        ref={placeDetailRef}
        style={{
          background: "none",
          border: "none",
          boxShadow: "none",
          maxWidth:
            window.innerWidth <= BREAKPOINTS.compactMax ? "100%" : "1080px",
          width: "fit-content",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header buttons: Publish (primary when unpublished), Gear menu, Close */}
        <div
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            display: "flex",
            gap: "8px",
            zIndex: 100,
          }}
        >
          {/* Publish button - prominent primary action when unpublished */}
          {!((selectedPlace as any)?.publish?.url || publishedUrl) && (
            <button
              onClick={onPublish}
              disabled={isPublishing}
              style={{
                background: isPublishing
                  ? "var(--color-primary-disabled)"
                  : "var(--color-primary)",
                border: "none",
                borderRadius: "6px",
                padding: "8px 14px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: isPublishing ? "not-allowed" : "pointer",
                fontSize: "13px",
                fontWeight: "500",
                color: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                transition: "all 0.2s",
              }}
              title={isPublishing ? "Publishing..." : "Publish place"}
              onMouseEnter={(e) => {
                if (!isPublishing) {
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.15)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
              {isPublishing ? "Publishing..." : "Publish"}
            </button>
          )}
          {/* Gear menu button */}
          <div ref={overlayGearRef} style={{ position: "relative" }}>
            <button
              onClick={onToggleGearMenu}
              style={{
                background: showOverlayGearMenu
                  ? "var(--color-primary)"
                  : "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                color: showOverlayGearMenu
                  ? "white"
                  : "var(--color-text-muted)",
                transition: "all 0.2s",
              }}
              title="Place options"
              onMouseEnter={(e) => {
                if (!showOverlayGearMenu) {
                  e.currentTarget.style.background =
                    "var(--color-surface-hover)";
                  e.currentTarget.style.color = "var(--color-primary)";
                }
              }}
              onMouseLeave={(e) => {
                if (!showOverlayGearMenu) {
                  e.currentTarget.style.background = "var(--color-surface)";
                  e.currentTarget.style.color = "var(--color-text-muted)";
                }
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
            {showOverlayGearMenu && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "4px",
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "8px",
                  boxShadow: "var(--popover-shadow, 0 4px 12px rgba(0,0,0,0.12))",
                  zIndex: 200,
                  minWidth: "160px",
                  padding: "4px",
                }}
              >
                {/* Secondary actions: Copy Link (when published), Unpublish (when published) */}
                {((selectedPlace as any)?.publish?.url || publishedUrl) && (
                  <>
                    <button
                      onClick={onCopyPublishedLink}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: "var(--color-text)",
                        textAlign: "left",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--color-surface-hover)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "none")
                      }
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                      Copy Published Link
                    </button>
                    <button
                      onClick={onUnpublish}
                      style={{
                        width: "100%",
                        padding: "8px 12px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "13px",
                        color: "var(--color-text-muted)",
                        textAlign: "left",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--color-surface-hover)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "none")
                      }
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                      Unpublish
                    </button>
                    {/* Divider before delete */}
                    <div
                      style={{
                        height: "1px",
                        background: "var(--color-border-faint)",
                        margin: "4px 0",
                      }}
                    />
                  </>
                )}
                <button
                  onClick={onDelete}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: "var(--color-danger)",
                    textAlign: "left",
                    borderRadius: "6px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(220,38,38,0.08)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "none")
                  }
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete Place
                </button>
              </div>
            )}
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: "50%",
              width: "36px",
              height: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              color: "var(--color-text-muted)",
            }}
            title="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {isEnriching ? (
          <div style={{ padding: "60px 40px", textAlign: "center" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "3px solid var(--color-border)",
                borderTop: "3px solid var(--color-primary)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 16px",
              }}
            ></div>
            <p style={{ color: "var(--color-text-muted)" }}>
              Loading place details...
            </p>
          </div>
        ) : enrichedCard ? (
          <>
            <PlaceDetailModule
              module={{ ...module, config: enrichedCard }}
              conversationId={conversationId}
              onClose={onClose}
              onNoteSaved={onNoteSaved}
            />
            {publishedUrl && (
              <div
                style={{
                  margin: "12px auto 0",
                  maxWidth: "900px",
                  width: "100%",
                  background: "#1a1a1a",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#2a8a60"
                  strokeWidth="2"
                  style={{ flexShrink: 0 }}
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#ccc",
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {publishedUrl}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(publishedUrl);
                  }}
                  style={{
                    background: "rgba(255,255,255,0.15)",
                    border: "none",
                    borderRadius: "4px",
                    padding: "4px 10px",
                    fontSize: "12px",
                    color: "white",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  Copy
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: "60px 40px", textAlign: "center" }}>
            <p style={{ color: "var(--color-text-muted)" }}>
              Failed to load place details
            </p>
          </div>
        )}
      </div>
    </div>,
    document.getElementById("modal-portal-root")!,
  );
}
